import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link, Redirect, type RouteComponentProps, withRouter } from 'react-router-dom';
import type { Subscription } from 'rxjs';
import { makeStyles } from 'tss-react/mui';

import * as authService from '~/services/authentication';
import { nullOrWhitespace } from '~/util/use-subscription';

const useStyles = makeStyles()({
  background: {
    marginTop: '30px',
    width: '100%',
    padding: '10px',
    alignContent: 'center',
    justifyContent: 'center',
  },

  control: {
    margin: '10px 0',
  },

  form: {
    width: '100%',
    padding: '1.5rem',
  },

  buttonContainer: {
    textAlign: 'end',
  },
  resetLink: {
    marginLeft: '24px',
    marginBottom: '16px',
    textDecoration: 'none',
  },
});

type LoginFormProps = {
  classes: ReturnType<typeof useStyles>['classes'];
};

interface LoginFormState {
  username: string;
  password: string;
  persistent: boolean;
  usernameError: boolean;
  passwordError: boolean;
  feedback: string;
  loggedIn: boolean;
  twoFA: string;
  credentialsOk: boolean;
  sentQRMail: boolean;
}

type WithRouterTypes = LoginFormProps & RouteComponentProps<{}>;

class LoginForm extends React.Component<WithRouterTypes, LoginFormState> {
  loggedInSub: Subscription;

  constructor(props: WithRouterTypes) {
    super(props);
    this.state = {
      username: '',
      password: '',
      persistent: false,
      usernameError: false,
      passwordError: false,
      feedback: '',
      loggedIn: false,
      twoFA: '',
      credentialsOk: false,
      sentQRMail: false,
    };
  }

  componentDidMount() {
    this.loggedInSub = authService.getUserObs().subscribe((user) => {
      this.setState({ loggedIn: user !== null });
    });
  }

  componentWillUnmount() {
    this.loggedInSub.unsubscribe();
  }

  handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { username, password, persistent, twoFA } = this.state;
    this.setState({ feedback: '' });

    const passwordError = nullOrWhitespace(password);
    const usernameError = nullOrWhitespace(username);
    this.setState({ passwordError, usernameError });
    if (passwordError || usernameError) {
      return;
    }

    try {
      if (!this.state.credentialsOk) {
        const loggedIn = await authService.credentialsCheck({ username, password }, persistent);
        if (!loggedIn) {
          this.setState({ credentialsOk: true });
        }
      } else {
        const twoFANumber = twoFA !== '' ? twoFA.replace(' ', '') : ' ';
        await authService.login({ username, password, twoFA: twoFANumber }, persistent);
      }
    } catch (error) {
      this.setState({ feedback: error.message });
    }
  };

  handleSendTwoFA = async () => {
    try {
      const res = await authService.sendTwoFA({
        username: this.state.username,
        password: this.state.password,
      });
      if (res.status === 200) {
        this.setState({ sentQRMail: true });
      }
    } catch (error) {
      this.setState({ feedback: error.message });
    }
  };

  handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ password: e.target.value });
    if (this.state.passwordError) {
      this.setState({ passwordError: false });
    }
  };

  handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ username: e.target.value });
    if (this.state.usernameError) {
      this.setState({ usernameError: false });
    }
  };

  handleTwoFAChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ twoFA: e.target.value });
  };

  render() {
    const { classes } = this.props;
    if (this.state.loggedIn) {
      const state = this.props.location.state;
      const loginTo = state?.loginTo ?? '/dimensions';
      return <Redirect to={loginTo} />;
    }

    const mailTooltipText = 'Klicka på den här knappen för att få QR-koden som ska skannas med din autentiserings app';

    const resetLink = (props: any) => <Link {...props} to="/resetpassword" />;
    return (
      <Grid container item xl={4} lg={5} md={7} xs={11}>
        <Paper className={classes.background}>
          <form className={classes.form} autoComplete="on" onSubmit={this.handleSubmit}>
            <Typography variant="h5">Logga in</Typography>
            <Grid container item xs={12} justifyContent="center">
              {!this.state.credentialsOk ? (
                <>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      className={classes.control}
                      fullWidth={true}
                      label="Användarnamn"
                      name="username"
                      id="username"
                      value={this.state.username}
                      onChange={this.handleUsernameChange}
                      error={this.state.usernameError}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth={true}
                      className={classes.control}
                      label="Lösenord"
                      name="password"
                      type="password"
                      id="password"
                      onChange={this.handlePasswordChange}
                      error={this.state.passwordError}
                    />
                  </Grid>
                  <Grid item xs={7}>
                    <FormControlLabel
                      label="Kom ihåg mig"
                      control={
                        <Checkbox
                          checked={this.state.persistent}
                          onChange={(e) => this.setState({ persistent: e.target.checked })}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={5} className={classes.buttonContainer}>
                    <Button color="primary" variant="contained" type="submit" value="LOGIN">
                      Logga in
                    </Button>
                  </Grid>
                </>
              ) : (
                <Grid container item xs={12} justifyContent={'space-between'} alignItems={'flex-end'}>
                  <Grid item xs={7} lg={7}>
                    <TextField
                      variant="standard"
                      fullWidth={true}
                      className={classes.control}
                      label="Kod från autentiserings app"
                      name="secret"
                      id="secret"
                      onChange={this.handleTwoFAChange}
                      value={this.state.twoFA
                        .toString()
                        .replace(/[^\dA-Z]/g, '')
                        .replace(/(.{3})/g, '$1 ')
                        .trim()}
                      slotProps={{
                        htmlInput: {
                          maxLength: 7,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={4} className={classes.buttonContainer}>
                    <Button color="primary" variant="contained" type="submit" value="LOGIN">
                      Logga in
                    </Button>
                  </Grid>
                  <Grid item xs={5} lg={7}>
                    <FormControlLabel
                      label="Kom ihåg mig"
                      control={
                        <Checkbox
                          checked={this.state.persistent}
                          onChange={(e) => this.setState({ persistent: e.target.checked })}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={5} className={classes.buttonContainer}>
                    {this.state.sentQRMail ? (
                      <Button color="primary" variant="outlined" disabled={true} endIcon={<CheckIcon />}>
                        Mail skickat
                      </Button>
                    ) : (
                      <Tooltip
                        arrow={true}
                        placement={'right-end'}
                        title={<Typography variant={'subtitle2'}> {mailTooltipText} </Typography>}
                      >
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={this.handleSendTwoFA}
                          endIcon={<InfoOutlinedIcon />}
                        >
                          Maila ny QR-kod
                        </Button>
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography color="error">{this.state.feedback}</Typography>
              </Grid>
            </Grid>
          </form>
          <Typography color="primary" className={classes.resetLink} component={resetLink}>
            Återställ lösenord
          </Typography>
        </Paper>
      </Grid>
    );
  }
}

const Wrapper = (props: Omit<WithRouterTypes, 'classes'>) => {
  const { classes } = useStyles();

  return <LoginForm classes={classes} {...props} />;
};

export default withRouter(Wrapper);
